import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { IProjectAOI } from "../../../../interfaces/i-project-aoi";
import { BaseTimelineComponent } from "./base-timeline.component";
import { debounceTime, Subject } from "rxjs";
import { FeatureFlagsService } from "../../../../services/internal-services/feature-flags.service";
import { MapService } from "../../../../services/internal-services/map.service";
import { GoogleAnalyticsService } from "../../../../../shared/services/google-analytics.service";
import { CdkConnectedOverlay } from "@angular/cdk/overlay";
import { OverlayRepositionService } from "../../../../services/internal-services/overlay-reposition.service";

@Directive()
export abstract class CollectionProjectSharedTimelineComponent
  extends BaseTimelineComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private _centerAOI: IProjectAOI;

  private _allAois: IProjectAOI[] = [];

  private infraredEnabled = false;

  private updateAOISSubject = new Subject<IProjectAOI[]>();

  private updateCenterAOISubject = new Subject<IProjectAOI>();

  isViewInitialized = false;

  @ViewChild(CdkConnectedOverlay) private connectedOverlay!: CdkConnectedOverlay;

  private overlayRepositionService = inject(OverlayRepositionService);

  protected constructor(
    public googleAnalyticsService: GoogleAnalyticsService,
    public cdr: ChangeDetectorRef,
    public flagService: FeatureFlagsService,
    public mapService: MapService,
  ) {
    super(googleAnalyticsService, cdr, flagService);
  }

  ngOnInit() {
    // add this to avoid to send multiple times the same event
    this.subscriptions.push(
      this.updateAOISSubject.pipe(debounceTime(this.debounceTimeMs)).subscribe((projects) => {
        this._allAois = projects;
        this.updateAois(projects);
        this.filterAoisByInfraredStatus();
      }),
    );
    this.subscriptions.push(
      this.updateCenterAOISubject.pipe(debounceTime(this.debounceTimeMs)).subscribe((project) => {
        this.updateCenterAOI(project);
      }),
    );
    this.subscriptions.push(
      this.viewerService.infraredStatus$.subscribe((enabled: boolean) => {
        this.infraredEnabled = enabled;
        this.filterAoisByInfraredStatus();
      }),
    );
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.isViewInitialized = true;
    this.overlayRepositionService.register(this.connectedOverlay);
    const projectStatus = this.viewerService.getProjectStatus();
    if (projectStatus) {
      const currentMapStatus = this.mapService.getCurrentMapStatus();

      const roundedCenter = projectStatus.mapStatus.center.geometry.coordinates.map((coord) => Math.round(coord));
      const currentRoundedCenter = currentMapStatus.center.map((coord) => Math.round(coord));
      const isSameZoom = Math.round(projectStatus.mapStatus.zoom) === Math.round(currentMapStatus.zoom);
      const isSameCenter = roundedCenter[0] === currentRoundedCenter[0] && roundedCenter[1] === currentRoundedCenter[1];

      if (isSameCenter && isSameZoom) {
        this.aois = projectStatus.enabledAOIs;
        this.centerAOI = projectStatus.centerAOI;
      }
    }
  }

  @Input()
  set aois(aois: IProjectAOI[]) {
    if (this.isViewInitialized) {
      this.updateAOISSubject.next(aois);
    }
  }

  @Input()
  set centerAOI(aoi: IProjectAOI) {
    if (this.isViewInitialized) {
      this.updateCenterAOISubject.next(aoi);
    }
  }

  get centerAOI(): IProjectAOI {
    return this._centerAOI;
  }

  private filterAoisByInfraredStatus(): void {
    if (this.infraredEnabled) {
      this._aois = this._allAois.filter((aoi) => aoi.isCirAvailable(false));
    } else {
      this._aois = [...this._allAois];
    }
    this.updateAois(this._aois);
  }

  updateAois(aois: IProjectAOI[]) {
    this._aois = aois;
    this.showSlider = !!this._aois?.length;
    this.showMiniSlider = this._aois?.length === 1;
    this.aoiPosition = [];
    this.preCalculateFixedDates(aois);
    if (this.sliderControl.value < 0) {
      this.sliderControl.reset(0);
    }
    this.createSliderOptions();
    this.updateSliderValue();
  }

  updateCenterAOI(aoi: IProjectAOI) {
    if (this._centerAOI?.getLayerId() !== aoi?.getLayerId()) {
      this._centerAOI = aoi;
      this.updateSliderValue();
      this.options = { ...this.options };
      this.cdr.detectChanges();
    }
  }

  updateSliderValue() {
    const newIndex =
      this.centerAOI && this._aois
        ? this._aois.findIndex((item: IProjectAOI) => item.getLayerId() === this.centerAOI.getLayerId())
        : -1;
    if (newIndex >= 0) {
      this.sliderControl.reset(newIndex);
      this.manualRefresh.emit(this.sliderControl.value);
    }
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.overlayRepositionService.unregister(this.connectedOverlay);
  }

  getLegend(value: number): string {
    let legend = "";
    const aoi = this._aois ? this._aois[value] : null;
    if (aoi && this.showProjectDetails(value)) {
      legend = `<br><span class="legend-limits">${aoi.getPrettyDate()}</span>`;
      if (this.enabledDebug) {
        legend += `<br><span>${aoi.getLayer()}</span> `;
        legend += `<br><span>${aoi.getLayerName()}</span> `;
      }
    } else if (aoi && this.showProjectYear(value)) {
      legend = `<span class="legend-year">${aoi.getYearShortFormat()}</span>`;
    }
    return legend;
  }
}
